/**
 * Asambleas — grabadora del Modo Sala (Fase 4).
 *
 * Graba una asamblea presencial con MediaRecorder y la sube MIENTRAS graba, de
 * a objetos de ~60 s, al bucket de asambleas por las URLs que emite el servidor
 * (asambleaAdmin salaUrlChunk). Está pensada para un celular de pie en una
 * sala: la red se corta, la pantalla se apaga y la asamblea dura 3 horas.
 *
 * - Formato: el primero que acepte el navegador (isTypeSupported) entre
 *   audio/webm;codecs=opus (Chrome, Android, Firefox) y audio/mp4 (Safari),
 *   mono, 48–64 kbps, sin cancelación de eco, supresión de ruido ni control
 *   automático de ganancia (el micrófono externo ya entrega el nivel justo y
 *   esos filtros "se comen" las voces lejanas).
 * - Partes: MediaRecorder entrega un bloque cada 10 s (timeslice) y 6 bloques
 *   forman un objeto (~60 s): asambleas/{aid}/audio/sala/{sesionId}/{n:05d}.{ext}.
 *   Son continuaciones del mismo stream: solo el 00000 trae el encabezado y el
 *   worker los concatena EN ORDEN. n es el índice del objeto y nunca cambia.
 * - Cola: se sube de a un objeto y en orden (el n+1 recién cuando el n quedó
 *   confirmado, así lo subido es siempre un prefijo contiguo), con reintentos
 *   exponenciales (1, 2, 4… s, hasta 1 minuto). Después de MAX_REINTENTOS
 *   fallos seguidos el objeto queda marcado con error (la pantalla lo avisa)
 *   pero se sigue intentando cada minuto, al volver la red (evento "online"),
 *   al volver a la pestaña o cuando un latido vuelve a pasar: el audio no se
 *   descarta nunca mientras la página siga abierta. Cada objeto se confirma con
 *   salaConfirmarChunk (el servidor mira que exista y pese más de 0). La URL
 *   firmada dura 15 minutos: se pide otra a los 10, o si el PUT la rechaza.
 * - Latido: salaLatido cada 15 s con el nivel en dB, `grabando` y la fase
 *   ("interrumpida": la captura se cortó sola y el cron avisa si nadie toca
 *   "Seguir grabando"; "deteniendo" / "subiendo" / "finalizando": paró a pedido
 *   y sube lo último). Cuando la fase cambia así late enseguida. El cron
 *   también avisa si una sesión deja de latir y la da por abandonada a los 2
 *   minutos.
 * - Pantalla: Wake Lock (crearWakeLock), que se vuelve a pedir al volver a la
 *   pestaña (el navegador lo suelta cuando se oculta).
 * - Corte del micrófono (se desenchufó el receptor, iOS terminó la pista):
 *   la sesión queda "interrumpida", lo grabado se sigue subiendo y la pantalla
 *   ofrece seguir en una sesión NUEVA (otra grabadora): un stream nuevo trae
 *   otro encabezado y no se puede pegar al anterior.
 * - Pausas de la captura (iOS al bloquear la pantalla, al cambiar de app o con
 *   una llamada: silencia la pista con "mute" y suspende la página): la
 *   grabación sigue en la misma sesión, pero ese rato no tiene audio. Si la
 *   pausa pasa de 5 s queda como hueco {desdeMs, hastaMs} (desde el inicio de
 *   la sesión, como el tMs de los marcadores): se manda con salaHueco y la
 *   pantalla lo muestra. Solo cuenta con evidencia de que se perdió audio (la
 *   pista se silenció, o el grabador no entregó nada mientras la página
 *   estuvo oculta): Android y las computadoras siguen grabando con la pestaña
 *   oculta, y eso no es un hueco.
 * - Otro dispositivo grabando la misma asamblea: salaIniciarSesion responde
 *   "otro_grabando"; iniciar({ forzar: true }) graba igual (lo confirma el
 *   staff). "Seguir grabando" abre la sesión nueva con continuaDe (la que este
 *   dispositivo termina de subir: el servidor no la confunde con una recarga).
 *   La sesión lleva el dispositivoId de este navegador (crearGrabadora
 *   { dispositivoId }: idDispositivo, en localStorage): así el servidor
 *   distingue una recarga (el mismo id) de otro equipo con la misma cuenta y
 *   el mismo navegador (dos iPhone con Safari).
 * - Memoria: todos los objetos quedan en memoria hasta que se cierra la página,
 *   para "Descargar una copia" si la red no vuelve (3 h a 48 kbps ≈ 65 MB).
 *   Una recarga pierde lo que no se subió y abre una sesión NUEVA (lo subido de
 *   la anterior igual se usa). Guardar en IndexedDB para sobrevivir a una
 *   recarga queda diferido.
 *
 * Todo lo del navegador (MediaRecorder, fetch, reloj, window, document,
 * AudioContext, navigator) es inyectable: los tests usan fakes.
 */
import { codigoDeError, detallesDeError, headersEnviables, mensajeDeError } from "./api";

/** Un dataavailable cada 10 s. */
export const TIMESLICE_MS = 10000;
/** 6 bloques de 10 s = un objeto de ~60 s. */
export const SLICES_POR_OBJETO = 6;
export const LATIDO_MS = 15000;
/** Fallos seguidos de un objeto antes de avisar que la subida está trabada (después sigue cada minuto). */
export const MAX_REINTENTOS = 8;
export const ESPERA_BASE_MS = 1000;
export const ESPERA_MAX_MS = 60000;
/** La URL firmada vive 15 min: se renueva a los 10. */
export const VIDA_URL_MS = 10 * 60 * 1000;
/** Un PUT colgado (red que no responde) se corta a los 2 minutos y se reintenta. */
export const TIMEOUT_PUT_MS = 2 * 60 * 1000;
/** Si el navegador no avisa el stop, se sigue con lo que haya. */
export const ESPERA_STOP_MS = 5000;
/** Veces que se vuelven a subir las partes que el servidor dice que faltan. */
export const MAX_RONDAS_FALTANTES = 3;
/** Una pausa de la captura más larga que esto queda como hueco de la grabación. */
export const PAUSA_MIN_MS = 5000;
/**
 * Al volver a la página se espera esto antes de dar por terminada una pausa
 * sin evidencia: los eventos de la pista (mute / unmute) que el navegador
 * encoló mientras la tenía suspendida pueden llegar después del visibilitychange.
 */
export const GRACIA_PAUSA_MS = 1500;
/** Huecos por sesión que se mandan al servidor (guarda hasta 100). */
export const MAX_HUECOS = 100;
/** Al finalizar, veces que se intenta mandar los huecos pendientes antes de cerrar igual. */
export const MAX_RONDAS_HUECOS = 3;

/**
 * Formatos en orden de preferencia (los acepta salaIniciarSesion). La
 * extensión final la devuelve el servidor.
 */
export const FORMATOS = Object.freeze([
  Object.freeze({ mimeType: "audio/webm;codecs=opus", ext: "webm", bitsPorSegundo: 48000 }),
  Object.freeze({ mimeType: "audio/mp4", ext: "mp4", bitsPorSegundo: 64000 }),
  Object.freeze({ mimeType: "audio/mp4;codecs=mp4a.40.2", ext: "mp4", bitsPorSegundo: 64000 }),
  Object.freeze({ mimeType: "audio/webm", ext: "webm", bitsPorSegundo: 48000 }),
]);

/** Clave de localStorage con el id de este navegador para el Modo Sala (idDispositivo). */
export const CLAVE_DISPOSITIVO = "habitia.modoSala.dispositivo";
/** La forma que acepta el servidor (salaIniciarSesion.dispositivoId). */
const DISPOSITIVO_RE = /^[A-Za-z0-9_-]{8,64}$/;
// Sin almacenamiento (bloqueado o con error): un id por carga de la página.
let dispositivoDeLaCarga = null;

/** Un UUID (crypto.randomUUID; si no está, 16 bytes al azar en hex; en último caso, Math.random). */
function idAlAzar() {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  try {
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      return Array.from(c.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (_) {
    /* sigue con Math.random */
  }
  let s = "";
  while (s.length < 32) s += Math.random().toString(36).slice(2);
  return s.slice(0, 32);
}

/**
 * Id estable de este navegador para el Modo Sala (dispositivoId de
 * salaIniciarSesion): un UUID guardado en localStorage
 * ("habitia.modoSala.dispositivo"), el mismo después de una recarga. Con él el
 * servidor distingue una recarga de otro equipo con la misma cuenta y el mismo
 * navegador (el User-Agent de dos iPhone con Safari 18 es igual). Si el
 * almacenamiento no se puede leer ni escribir (bloqueado, navegación privada
 * estricta), uno por carga de la página: una recarga ahí parece otro equipo y
 * pide confirmar, que es lo seguro.
 * @param {Storage|null} [almacen]  por defecto window.localStorage; null = sin almacenamiento
 * @returns {string}
 */
export function idDispositivo(almacen) {
  let a = almacen;
  if (a === undefined) {
    try {
      a = typeof window !== "undefined" ? window.localStorage : null;
    } catch (_) {
      a = null;
    }
  }
  try {
    const guardado = a ? a.getItem(CLAVE_DISPOSITIVO) : null;
    if (typeof guardado === "string" && DISPOSITIVO_RE.test(guardado)) return guardado;
  } catch (_) {
    /* sin almacenamiento: el de esta carga */
  }
  if (!dispositivoDeLaCarga) dispositivoDeLaCarga = idAlAzar();
  try {
    if (a) a.setItem(CLAVE_DISPOSITIVO, dispositivoDeLaCarga);
  } catch (_) {
    /* queda el de esta carga */
  }
  return dispositivoDeLaCarga;
}

/** Reloj del navegador (los tests inyectan uno falso con la misma forma). */
export const relojReal = Object.freeze({
  ahora: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
});

// Errores de callables que vale la pena reintentar: la red, el servidor
// ocupado o la sesión de Firebase que se cerró (se vuelve a entrar sin perder
// la grabación). Los demás (failed-precondition, invalid-argument…) son
// respuestas del servidor.
const CODIGOS_REINTENTABLES = new Set([
  "unavailable",
  "deadline-exceeded",
  "internal",
  "unknown",
  "cancelled",
  "aborted",
  "resource-exhausted",
  "unauthenticated",
  "permission-denied",
]);
const CODIGOS_DE_RED = new Set(["unavailable", "deadline-exceeded", "internal", "unknown", "cancelled"]);

/** ¿El error es de la red (sin conexión, timeout) y no una respuesta del servidor? */
export function esErrorDeRed(e) {
  if (!e) return false;
  if (e.red === true) return true;
  if (e.name === "TypeError" || e.name === "AbortError") return true;
  const c = codigoDeError(e);
  return !c || CODIGOS_DE_RED.has(c);
}

/** ¿Vale la pena reintentar la llamada? */
export function esReintentable(e) {
  if (esErrorDeRed(e)) return true;
  return CODIGOS_REINTENTABLES.has(codigoDeError(e));
}

/** Espera antes del intento número `intentos` (1 → 1 s, 2 → 2 s, 3 → 4 s… hasta 60 s). */
export function esperaReintento(intentos) {
  const n = Math.max(1, Math.floor(Number(intentos) || 1));
  return Math.min(ESPERA_MAX_MS, ESPERA_BASE_MS * 2 ** (n - 1));
}

/** "audio/webm;codecs=opus" → "audio/webm" (el Content-Type que firma el servidor). */
export function tipoBase(mimeType) {
  return String(mimeType || "").split(";")[0].trim();
}

// ─────────────────────────── Formato y micrófono ───────────────────────────

/**
 * Primer formato de FORMATOS que el navegador sabe grabar, o null (sin
 * MediaRecorder o sin isTypeSupported: no se puede saber qué saldría).
 */
export function elegirFormato(MediaRecorderCls) {
  if (!MediaRecorderCls || typeof MediaRecorderCls.isTypeSupported !== "function") return null;
  for (const f of FORMATOS) {
    try {
      if (MediaRecorderCls.isTypeSupported(f.mimeType)) return { ...f };
    } catch (_) {
      /* un navegador viejo que tira con algún tipo: probar el siguiente */
    }
  }
  return null;
}

/**
 * Restricciones de getUserMedia: mono y sin procesamiento de voz (eco, ruido,
 * ganancia automática), opcionalmente con una entrada exacta.
 */
export function restriccionesAudio(deviceId) {
  const audio = { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 };
  if (deviceId) audio.deviceId = { exact: deviceId };
  return { audio, video: false };
}

/**
 * Motivo legible de un error de getUserMedia:
 *   "permiso" | "sin_microfono" | "ocupado" | "inseguro" | "otro".
 */
export function motivoErrorMicrofono(e) {
  const n = e && e.name;
  if (n === "NotAllowedError" || n === "PermissionDeniedError") return "permiso";
  if (n === "NotFoundError" || n === "DevicesNotFoundError" || n === "OverconstrainedError") return "sin_microfono";
  if (n === "NotReadableError" || n === "TrackStartError" || n === "AbortError") return "ocupado";
  if (n === "SecurityError") return "inseguro";
  return "otro";
}

/**
 * Pide el micrófono (la entrada elegida, o la del sistema). Sin mediaDevices
 * (página sin https) tira un SecurityError: el micrófono solo anda en un
 * contexto seguro (https, o localhost en el entorno local).
 */
export async function pedirMicrofono({ mediaDevices, deviceId } = {}) {
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
    const e = new Error("El navegador no da acceso al micrófono en esta página.");
    e.name = "SecurityError";
    throw e;
  }
  return mediaDevices.getUserMedia(restriccionesAudio(deviceId));
}

// Etiquetas típicas de un micrófono externo (receptor inalámbrico, grabador
// USB, de solapa) y de los micrófonos del propio equipo.
const EXTERNA_RE =
  /\b(usb|dji|r[oø]de|zoom|h1n?|h1essential|wireless|inal[aá]mbric[oa]|lav(alier)?|solapa|receptor|receiver|rx|bluetooth|headset|auricular(es)?|airpods|jabra|shure|boya|saramonic|hollyland|external|extern[oa])\b/i;
const INTERNA_RE = /(built-?in|intern[oa]|integrad[oa]|iphone|ipad|macbook|predeterminad|default|communications|comunicaciones)/i;

/** ¿La etiqueta parece de un micrófono externo? (sin etiqueta —antes del permiso— no se sabe) */
export function esEntradaExterna(label) {
  const l = String(label || "");
  if (!l) return false;
  if (INTERNA_RE.test(l) && !/\b(usb|dji|zoom)\b/i.test(l)) return false;
  return EXTERNA_RE.test(l);
}

/**
 * Entradas de audio reales (sin las pseudoentradas "default" y
 * "communications" de Chrome): [{ deviceId, label, externa }]. Las etiquetas
 * llegan recién después del permiso.
 */
export async function listarEntradas(mediaDevices) {
  if (!mediaDevices || typeof mediaDevices.enumerateDevices !== "function") return [];
  let lista = [];
  try {
    lista = (await mediaDevices.enumerateDevices()) || [];
  } catch (_) {
    return [];
  }
  const vistos = new Set();
  const out = [];
  lista.forEach((d) => {
    if (!d || d.kind !== "audioinput" || !d.deviceId) return;
    if (d.deviceId === "default" || d.deviceId === "communications" || vistos.has(d.deviceId)) return;
    vistos.add(d.deviceId);
    const label = String(d.label || "");
    out.push({ deviceId: d.deviceId, label, externa: esEntradaExterna(label) });
  });
  return out;
}

/** La entrada que conviene usar: la primera externa (o null si no hay ninguna). */
export function sugerirEntrada(entradas) {
  return (entradas || []).find((e) => e && e.externa) || null;
}

/** deviceId y etiqueta de la pista de audio de un stream. */
export function entradaDelStream(stream) {
  const pista = stream && typeof stream.getAudioTracks === "function" ? stream.getAudioTracks()[0] : null;
  if (!pista) return { deviceId: null, label: "" };
  let ajustes = {};
  try {
    ajustes = typeof pista.getSettings === "function" ? pista.getSettings() || {} : {};
  } catch (_) {
    ajustes = {};
  }
  return { deviceId: ajustes.deviceId || null, label: String(pista.label || "") };
}

/** ¿El stream tiene una pista de audio viva? */
export function streamVivo(stream) {
  const pistas = stream && typeof stream.getAudioTracks === "function" ? stream.getAudioTracks() : [];
  return pistas.some((p) => p && p.readyState !== "ended");
}

/** Corta todas las pistas de un stream (suelta el micrófono). */
export function cerrarStream(stream) {
  const pistas = stream && typeof stream.getTracks === "function" ? stream.getTracks() : [];
  pistas.forEach((p) => {
    try {
      p.stop();
    } catch (_) {
      /* ya terminada */
    }
  });
}

// ─────────────────────────────── Medidor ───────────────────────────────────

/** Nivel RMS en dBFS (−100 a 0, entero) de muestras en [−1, 1]; null sin muestras. */
export function dbDeMuestras(muestras) {
  if (!muestras || !muestras.length) return null;
  let suma = 0;
  for (let i = 0; i < muestras.length; i += 1) suma += muestras[i] * muestras[i];
  const rms = Math.sqrt(suma / muestras.length);
  if (!(rms > 0)) return -100;
  return Math.max(-100, Math.min(0, Math.round(20 * Math.log10(rms))));
}

/**
 * Medidor de nivel con un AnalyserNode. El grafo termina en un GainNode en 0
 * conectado a la salida: algunos navegadores no procesan un grafo que no llega
 * al destino, y con ganancia 0 el micrófono no suena por el parlante.
 * Sin AudioContext devuelve un medidor que siempre da null.
 * @returns {{nivelDb: () => (number|null), reanudar: () => Promise<void>, cerrar: () => void}}
 */
export function crearMedidor(stream, { AudioContext: Ctx } = {}) {
  const nulo = { nivelDb: () => null, reanudar: async () => {}, cerrar: () => {} };
  if (!Ctx || !stream) return nulo;
  let ctx = null;
  try {
    ctx = new Ctx();
    const fuente = ctx.createMediaStreamSource(stream);
    const analizador = ctx.createAnalyser();
    analizador.fftSize = 2048;
    const silencio = ctx.createGain();
    silencio.gain.value = 0;
    fuente.connect(analizador);
    analizador.connect(silencio);
    silencio.connect(ctx.destination);
    const flotante = typeof analizador.getFloatTimeDomainData === "function";
    const buf = flotante ? new Float32Array(analizador.fftSize) : new Uint8Array(analizador.fftSize);
    return {
      nivelDb() {
        try {
          if (flotante) {
            analizador.getFloatTimeDomainData(buf);
            return dbDeMuestras(buf);
          }
          analizador.getByteTimeDomainData(buf);
          return dbDeMuestras(Array.from(buf, (v) => (v - 128) / 128));
        } catch (_) {
          return null;
        }
      },
      async reanudar() {
        try {
          if (ctx.state === "suspended") await ctx.resume();
        } catch (_) {
          /* iOS: se reanuda con el próximo toque */
        }
      },
      cerrar() {
        try {
          fuente.disconnect();
        } catch (_) {
          /* ya desconectado */
        }
        try {
          ctx.close();
        } catch (_) {
          /* ya cerrado */
        }
      },
    };
  } catch (_) {
    try {
      if (ctx) ctx.close();
    } catch (__) {
      /* nada */
    }
    return nulo;
  }
}

// ─────────────────────────────── Wake Lock ─────────────────────────────────

/**
 * Pantalla siempre encendida (Screen Wake Lock). El navegador la suelta al
 * ocultar la pestaña (o al bloquear): mientras se la quiera, se vuelve a pedir
 * en cada visibilitychange a "visible".
 */
export function crearWakeLock({ navigator: nav, document: doc } = {}) {
  const api = nav && nav.wakeLock && typeof nav.wakeLock.request === "function" ? nav.wakeLock : null;
  const subs = new Set();
  let sentinela = null;
  let deseado = false;
  let pidiendo = null;
  let destruido = false;

  const avisar = () =>
    subs.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        /* un suscriptor roto no frena a los demás */
      }
    });
  const activo = () => !!sentinela && sentinela.released !== true;
  const soltarSentinela = (s) => {
    try {
      Promise.resolve(s.release()).catch(() => {});
    } catch (_) {
      /* ya suelta */
    }
  };

  function pedir() {
    deseado = true;
    if (!api || destruido) return Promise.resolve(false);
    if (activo()) return Promise.resolve(true);
    if (doc && doc.visibilityState === "hidden") return Promise.resolve(false);
    if (pidiendo) return pidiendo;
    pidiendo = (async () => {
      try {
        const s = await api.request("screen");
        if (!deseado || destruido) {
          soltarSentinela(s);
          return false;
        }
        sentinela = s;
        if (s && typeof s.addEventListener === "function") {
          s.addEventListener("release", () => {
            if (sentinela === s) sentinela = null;
            avisar();
          });
        }
        return true;
      } catch (_) {
        return false;
      } finally {
        pidiendo = null;
        avisar();
      }
    })();
    return pidiendo;
  }

  function soltar() {
    deseado = false;
    const s = sentinela;
    sentinela = null;
    if (s) soltarSentinela(s);
    avisar();
  }

  const alCambiarVisibilidad = () => {
    if (deseado && !destruido && doc && doc.visibilityState === "visible") pedir();
  };
  if (doc && typeof doc.addEventListener === "function") doc.addEventListener("visibilitychange", alCambiarVisibilidad);

  return {
    soportado: !!api,
    activo,
    pedir,
    soltar,
    suscribir(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    destruir() {
      soltar();
      destruido = true;
      if (doc && typeof doc.removeEventListener === "function") doc.removeEventListener("visibilitychange", alCambiarVisibilidad);
      subs.clear();
    },
  };
}

// ─────────────────────────────── Grabadora ─────────────────────────────────

/**
 * Grabadora de una sesión de sala (un stream continuo = una sesión del
 * servidor).
 *
 * @param {object} p
 * @param {string} p.aid
 * @param {MediaStream} p.stream          el micrófono ya elegido (lo cierra quien lo abrió)
 * @param {{mimeType: string, ext: string, bitsPorSegundo: number}} p.formato   de elegirFormato
 * @param {object} p.api   callables (ver api.js): salaIniciarSesion, salaUrlChunk,
 *                         salaConfirmarChunk, salaLatido, salaFinalizarSesion y
 *                         salaHueco (si falta, los huecos quedan solo en la pantalla);
 *                         cada uno recibe un objeto con los mismos campos que el callable.
 * @param {object} [p.deps]  { MediaRecorder, fetch, reloj, ventana, documento }
 * @param {object} [p.medidor]   crearMedidor (para el nivel del latido)
 * @param {object} [p.wakeLock]  crearWakeLock (se pide al empezar)
 * @param {string|null} [p.dispositivoId]  idDispositivo(): viaja al abrir la sesión (sin él, el servidor compara el navegador)
 *
 * Fases: inactiva → iniciando → grabando → deteniendo → subiendo → finalizando
 * → completa; "interrumpida" si el micrófono se cortó solo; "error" si el
 * servidor rechazó el cierre o el navegador no pudo grabar.
 */
export function crearGrabadora({ aid, stream, formato, api, deps = {}, medidor = null, wakeLock = null, dispositivoId = null }) {
  const MR = deps.MediaRecorder;
  const fetchFn = deps.fetch;
  const reloj = deps.reloj || relojReal;
  const ventana = deps.ventana !== undefined ? deps.ventana : typeof window !== "undefined" ? window : null;
  const doc = deps.documento !== undefined ? deps.documento : typeof document !== "undefined" ? document : null;
  if (!formato || !formato.mimeType) throw new Error("Falta el formato de grabación.");

  const subs = new Set();
  const objetos = [];
  let partes = [];
  let slices = 0;
  let recorder = null;
  let fase = "inactiva";
  let sesionId = null;
  let ext = formato.ext;
  let iniciadaEnMs = null;
  let detenidaEnMs = null;
  let ultimoDatoMs = null;
  let detenida = false;
  let interrupcion = null; // null | "microfono" | "error"
  let microfono = "ok"; // ok | silenciado | cortado
  let ultimoError = null;
  let sinConexion = false;
  let latidoFallando = false;
  let sesionRechazada = false;
  let resultado = null;
  let destruida = false;
  let bombeando = false;
  let timerReintento = null;
  let timerLatido = null;
  let timerVigia = null;
  let esperasCola = [];
  let finalizando = null;
  let resolverStop = null;
  // Pausa de la captura en curso: { desdeMs, desdeAbsMs, oculta, silenciada, primerDatoMs, vueltaMs }.
  let pausa = null;
  let timerPausa = null;
  // Huecos de la sesión: { desdeMs, hastaMs, causa, estado: "pendiente" | "enviado" | "rechazado", intentos }.
  const huecos = [];
  let enviandoHuecos = false;
  let timerHuecos = null;
  const pistas = stream && typeof stream.getAudioTracks === "function" ? stream.getAudioTracks() : [];

  const avisar = () =>
    subs.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        /* un suscriptor roto no frena la grabación */
      }
    });

  const dormir = (ms) => new Promise((res) => reloj.setTimeout(res, ms));

  /** Espera la promesa, o sigue a los `ms` (y limpia el timer). */
  const conTope = (promesa, ms) => {
    let t = null;
    const tope = new Promise((res) => {
      t = reloj.setTimeout(res, ms);
    });
    return Promise.race([promesa, tope]).finally(() => reloj.clearTimeout(t));
  };

  // ── Objetos ──

  function cerrarObjeto() {
    slices = 0;
    if (!partes.length) return;
    const blob = new Blob(partes, { type: formato.mimeType });
    partes = [];
    objetos.push({
      n: objetos.length,
      blob,
      bytes: blob.size,
      estado: "en_cola", // en_cola | subiendo | confirmado
      intentos: 0,
      reintentoEnMs: 0,
      url: null,
      urlEnMs: 0,
      subido: false,
      conError: false,
      error: null,
    });
    bombear();
  }

  const siguientePendiente = () => objetos.find((o) => o.estado !== "confirmado") || null;
  const colaVacia = () => detenida && partes.length === 0 && objetos.every((o) => o.estado === "confirmado");

  function revisarEsperas() {
    if (!esperasCola.length || !(colaVacia() || destruida)) return;
    const lista = esperasCola;
    esperasCola = [];
    lista.forEach((r) => r());
  }

  function esperarCola() {
    if (colaVacia() || destruida) return Promise.resolve();
    return new Promise((res) => esperasCola.push(res));
  }

  // ── Subida ──

  function headersDelPut(headers) {
    const h = headersEnviables(headers);
    if (!Object.keys(h).some((k) => k.toLowerCase() === "content-type")) h["Content-Type"] = tipoBase(formato.mimeType);
    return h;
  }

  async function put(url, blob, headers) {
    if (typeof fetchFn !== "function") throw new Error("Este navegador no puede subir archivos.");
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    let t = null;
    const vencido = new Promise((_, rej) => {
      t = reloj.setTimeout(() => {
        if (ctrl) ctrl.abort();
        const e = new Error("La subida tardó demasiado.");
        e.red = true;
        rej(e);
      }, TIMEOUT_PUT_MS);
    });
    try {
      return await Promise.race([
        fetchFn(url, { method: "PUT", headers: headersDelPut(headers), body: blob, ...(ctrl ? { signal: ctrl.signal } : {}) }),
        vencido,
      ]);
    } finally {
      reloj.clearTimeout(t);
    }
  }

  async function subir(obj) {
    obj.estado = "subiendo";
    avisar();
    try {
      if (!obj.subido) {
        if (!obj.url || reloj.ahora() - obj.urlEnMs > VIDA_URL_MS) {
          obj.url = null;
          const r = await api.salaUrlChunk({ aid, sesionId, n: obj.n, bytes: obj.bytes });
          if (!r || typeof r.uploadUrl !== "string" || !r.uploadUrl) throw new Error("El servidor no devolvió dónde subir la grabación.");
          obj.url = r;
          obj.urlEnMs = reloj.ahora();
        }
        const resp = await put(obj.url.uploadUrl, obj.blob, obj.url.headers);
        if (!resp || !resp.ok) {
          const status = resp ? Number(resp.status) || 0 : 0;
          // Firma vencida o rechazada: la próxima vez se pide otra URL.
          if (status === 400 || status === 401 || status === 403) obj.url = null;
          const e = new Error(`No se pudo subir una parte de la grabación (error ${status}).`);
          e.red = status === 0 || status === 408 || status === 429 || status >= 500;
          throw e;
        }
        obj.subido = true;
      }
      try {
        await api.salaConfirmarChunk({ aid, sesionId, n: obj.n });
      } catch (e) {
        // Si el servidor respondió (no la red), puede que no vea el objeto: se vuelve a subir.
        if (!esErrorDeRed(e)) obj.subido = false;
        throw e;
      }
      obj.estado = "confirmado";
      obj.intentos = 0;
      obj.conError = false;
      obj.error = null;
      // Si subió, hay red (aunque el último latido haya fallado).
      sinConexion = false;
      latidoFallando = false;
      sesionRechazada = false;
      return true;
    } catch (e) {
      obj.estado = "en_cola";
      obj.intentos += 1;
      obj.error = mensajeDeError(e);
      if (obj.intentos >= MAX_REINTENTOS) obj.conError = true;
      if (codigoDeError(e) === "failed-precondition") sesionRechazada = true;
      sinConexion = esErrorDeRed(e);
      ultimoError = obj.error;
      return false;
    }
  }

  function programarReintento(ms) {
    if (timerReintento) reloj.clearTimeout(timerReintento);
    timerReintento = reloj.setTimeout(() => {
      timerReintento = null;
      bombear();
    }, Math.max(0, ms));
  }

  async function bombear() {
    if (bombeando || destruida || !sesionId) return;
    bombeando = true;
    try {
      for (;;) {
        const obj = siguientePendiente();
        if (!obj) break;
        const espera = obj.reintentoEnMs - reloj.ahora();
        if (espera > 0) {
          programarReintento(espera);
          break;
        }
        const ok = await subir(obj);
        if (destruida) return;
        if (!ok) {
          obj.reintentoEnMs = reloj.ahora() + esperaReintento(obj.intentos);
          programarReintento(obj.reintentoEnMs - reloj.ahora());
          avisar();
          break;
        }
        avisar();
      }
    } finally {
      bombeando = false;
    }
    revisarEsperas();
  }

  /** Reintenta ya la parte trabada (volvió la red, se volvió a la pestaña). */
  function reintentarAhora() {
    const obj = siguientePendiente();
    if (obj) obj.reintentoEnMs = 0;
    if (timerReintento) {
      reloj.clearTimeout(timerReintento);
      timerReintento = null;
    }
    bombear();
  }

  // ── Latido y vigía ──

  function pararLatido() {
    if (timerLatido) reloj.clearTimeout(timerLatido);
    timerLatido = null;
  }

  /**
   * Latido cada 15 s. Manda la fase: "interrumpida" (la captura se cortó sola:
   * el servidor avisa si nadie sigue grabando) no es lo mismo que "deteniendo"
   * / "subiendo" / "finalizando" (paró a pedido y termina de subir). Cuando la
   * fase cambia así se late enseguida (ms 0), sin esperar los 15 s.
   */
  function programarLatido(ms = LATIDO_MS) {
    pararLatido();
    timerLatido = reloj.setTimeout(async () => {
      timerLatido = null;
      if (destruida || fase === "completa" || fase === "error") return;
      const nivel = medidor && typeof medidor.nivelDb === "function" ? medidor.nivelDb() : null;
      try {
        await api.salaLatido({ aid, sesionId, ...(Number.isFinite(nivel) ? { nivelDb: nivel } : {}), grabando: fase === "grabando", fase });
        latidoFallando = false;
        // La red volvió: si una parte (o un hueco) esperaba su próximo intento, que no espere.
        const obj = siguientePendiente();
        if (obj && obj.estado === "en_cola" && obj.reintentoEnMs > reloj.ahora()) reintentarAhora();
        if (timerHuecos) enviarHuecosYa();
      } catch (e) {
        latidoFallando = esErrorDeRed(e);
        if (codigoDeError(e) === "failed-precondition") sesionRechazada = true;
      }
      if (destruida) return;
      avisar();
      if (fase !== "completa" && fase !== "error" && !timerLatido) programarLatido();
    }, Math.max(0, ms));
  }

  function pararVigia() {
    if (timerVigia) reloj.clearTimeout(timerVigia);
    timerVigia = null;
  }

  // Algunos navegadores no respetan el timeslice: si pasan 2 bloques sin
  // datos se piden a mano (requestData), así nada queda solo en el grabador.
  function programarVigia() {
    pararVigia();
    timerVigia = reloj.setTimeout(() => {
      timerVigia = null;
      if (destruida || fase !== "grabando" || !recorder || recorder.state !== "recording") return;
      if (reloj.ahora() - ultimoDatoMs >= TIMESLICE_MS * 2 && typeof recorder.requestData === "function") {
        try {
          recorder.requestData();
        } catch (_) {
          /* sin requestData: se espera al próximo bloque */
        }
      }
      programarVigia();
    }, TIMESLICE_MS);
  }

  // ── Pausas de la captura y huecos ──

  const pistaSilenciada = () => pistas.some((p) => p && p.muted === true);
  const paginaOculta = () => !!doc && doc.visibilityState === "hidden";
  const huecosPendientes = () => huecos.some((h) => h.estado === "pendiente");
  // El servidor no va a aceptar más (tope de huecos o de la sesión): no se reintenta.
  const esTope = (e) => /^max_/.test(String(detallesDeError(e).motivo || ""));

  /** Empieza (o completa) una pausa mientras se graba: la página se ocultó o la pista se silenció. */
  function abrirPausa({ oculta = false, silenciada = false }) {
    if (fase !== "grabando") return;
    if (!pausa) pausa = { desdeMs: tiempoMs(), desdeAbsMs: reloj.ahora(), oculta: false, silenciada: false, primerDatoMs: null, vueltaMs: null };
    if (oculta) pausa.oculta = true;
    if (silenciada) pausa.silenciada = true;
  }

  function soltarTimerPausa() {
    if (timerPausa) reloj.clearTimeout(timerPausa);
    timerPausa = null;
  }

  /**
   * Termina la pausa en `hastaMs` (desde el inicio de la sesión). Queda como
   * hueco si pasó de PAUSA_MIN_MS y hay evidencia de que se perdió audio: la
   * pista se silenció, o el grabador no entregó nada durante 1,5 bloques con
   * la página oculta (el navegador la suspendió). Sin eso (Android o una
   * computadora con la pestaña oculta siguen grabando) no es un hueco.
   */
  function cerrarPausa(hastaMs = tiempoMs()) {
    soltarTimerPausa();
    const p = pausa;
    pausa = null;
    if (!p) return;
    const sinDatosMs = (p.primerDatoMs != null ? p.primerDatoMs : reloj.ahora()) - p.desdeAbsMs;
    const evidencia = p.silenciada || (p.oculta && sinDatosMs > TIMESLICE_MS * 1.5);
    if (!evidencia || hastaMs - p.desdeMs < PAUSA_MIN_MS) return;
    agregarHueco(p.desdeMs, hastaMs, p.silenciada ? "silenciado" : "oculta");
  }

  /** La página volvió a estar visible con una pausa abierta. */
  function revisarPausaAlVolver() {
    if (!pausa) return;
    pausa.vueltaMs = tiempoMs();
    // Sigue silenciada (una llamada que sigue): termina con el "unmute".
    if (pistaSilenciada()) {
      pausa.silenciada = true;
      return;
    }
    if (pausa.silenciada) {
      cerrarPausa(pausa.vueltaMs);
      return;
    }
    // Sin evidencia todavía: un momento para los eventos que el navegador encoló.
    soltarTimerPausa();
    timerPausa = reloj.setTimeout(() => {
      timerPausa = null;
      if (!pausa || destruida || paginaOculta()) return;
      if (pistaSilenciada()) {
        pausa.silenciada = true;
        return;
      }
      cerrarPausa(pausa.vueltaMs != null ? pausa.vueltaMs : tiempoMs());
      avisar();
    }, GRACIA_PAUSA_MS);
  }

  /** causa: "silenciado" (el sistema silenció la pista) | "oculta" (la página estuvo suspendida). */
  function agregarHueco(desdeMs, hastaMs, causa) {
    huecos.push({
      desdeMs: Math.max(0, Math.round(desdeMs)),
      hastaMs: Math.max(0, Math.round(hastaMs)),
      causa,
      // Más allá del tope el servidor no los guarda: quedan solo en la pantalla.
      estado: huecos.length < MAX_HUECOS && typeof api.salaHueco === "function" ? "pendiente" : "rechazado",
      intentos: 0,
    });
    enviarHuecos();
  }

  function programarHuecos(ms) {
    if (timerHuecos) reloj.clearTimeout(timerHuecos);
    timerHuecos = reloj.setTimeout(() => {
      timerHuecos = null;
      enviarHuecos();
    }, Math.max(0, ms));
  }

  /** Reintenta ya los huecos que esperaban (volvió la red). */
  function enviarHuecosYa() {
    if (timerHuecos) reloj.clearTimeout(timerHuecos);
    timerHuecos = null;
    return enviarHuecos();
  }

  /**
   * Manda los huecos pendientes en orden. Un error de red se reintenta (con
   * espera exponencial, al volver la red o con el próximo latido que pase); si
   * el servidor lo rechaza, queda solo en la pantalla.
   */
  async function enviarHuecos() {
    if (enviandoHuecos || destruida || !sesionId) return;
    enviandoHuecos = true;
    try {
      for (const h of huecos) {
        if (h.estado !== "pendiente") continue;
        try {
          const r = await api.salaHueco({ aid, sesionId, desdeMs: h.desdeMs, hastaMs: h.hastaMs, causa: h.causa });
          // guardado: false → la sesión ya tenía el tope de huecos: queda solo en la pantalla.
          h.estado = r && r.guardado === false ? "rechazado" : "enviado";
        } catch (e) {
          if (destruida) return;
          if (!esReintentable(e) || esTope(e)) {
            h.estado = "rechazado";
            continue;
          }
          h.intentos += 1;
          programarHuecos(esperaReintento(h.intentos));
          break;
        }
      }
    } finally {
      enviandoHuecos = false;
      if (!destruida) avisar();
    }
  }

  // ── Eventos del grabador, las pistas y la página ──

  function conectar(rec) {
    rec.ondataavailable = (ev) => {
      if (destruida) return;
      ultimoDatoMs = reloj.ahora();
      // Primer bloque que llega con la pausa abierta (no el que el navegador entrega justo al ocultarse).
      if (pausa && pausa.primerDatoMs == null && ultimoDatoMs - pausa.desdeAbsMs >= 1000) pausa.primerDatoMs = ultimoDatoMs;
      const blob = ev && ev.data;
      if (blob && blob.size > 0) partes.push(blob);
      slices += 1;
      if (slices >= SLICES_POR_OBJETO) cerrarObjeto();
      avisar();
    };
    rec.onstop = () => {
      if (destruida) return;
      cerrarObjeto();
      detenida = true;
      detenidaEnMs = reloj.ahora();
      pararVigia();
      if (resolverStop) {
        const r = resolverStop;
        resolverStop = null;
        r();
      } else if (fase === "grabando") {
        // Paró solo: se cortó el micrófono o el navegador cortó la captura. El
        // servidor se entera con un latido ya (no espera al próximo). Una pausa
        // abierta no es un hueco: la sesión termina acá (el resto es el corte
        // entre sesiones, que ve el worker).
        interrupcion = interrupcion || "microfono";
        fase = "interrumpida";
        soltarTimerPausa();
        pausa = null;
        programarLatido(0);
      }
      avisar();
      bombear();
      revisarEsperas();
    };
    rec.onerror = (ev) => {
      if (destruida) return;
      interrupcion = "error";
      ultimoError = (ev && ev.error && ev.error.message) || "Se cortó la grabación.";
      try {
        if (rec.state !== "inactive") rec.stop();
      } catch (_) {
        /* ya parado */
      }
      avisar();
    };
  }

  const alTerminarPista = () => {
    if (destruida || fase !== "grabando") return;
    interrupcion = "microfono";
    microfono = "cortado";
    // La sesión termina acá: lo que siga hasta la próxima sesión es el hueco
    // entre sesiones (lo ve el worker), no una pausa dentro de esta.
    soltarTimerPausa();
    pausa = null;
    try {
      if (recorder && recorder.state !== "inactive") recorder.stop();
    } catch (_) {
      /* ya parado */
    }
    avisar();
  };
  const alSilenciar = () => {
    if (destruida) return;
    if (microfono === "ok") microfono = "silenciado";
    abrirPausa({ silenciada: true });
    avisar();
  };
  const alReactivar = () => {
    if (destruida) return;
    if (microfono === "silenciado") microfono = "ok";
    // La captura volvió; con la página todavía oculta, la pausa sigue hasta que vuelva.
    if (pausa && !paginaOculta()) cerrarPausa();
    avisar();
  };
  const alCambiarVisibilidad = () => {
    if (!doc || destruida) return;
    if (doc.visibilityState === "hidden") {
      // iOS suspende la página (y silencia la captura) al bloquear la pantalla o cambiar de app.
      abrirPausa({ oculta: true, silenciada: pistaSilenciada() });
      return;
    }
    if (doc.visibilityState !== "visible") return;
    // Al volver: si la pista murió mientras estaba oculta, cortar prolijo; y reintentar lo trabado.
    if (fase === "grabando" && pistas.length && pistas.every((p) => p && p.readyState === "ended")) alTerminarPista();
    else revisarPausaAlVolver();
    reintentarAhora();
    if (huecosPendientes()) enviarHuecosYa();
    avisar();
  };
  const alVolverLaRed = () => {
    reintentarAhora();
    if (huecosPendientes()) enviarHuecosYa();
  };

  function escuchar(on) {
    const metodo = on ? "addEventListener" : "removeEventListener";
    pistas.forEach((p) => {
      if (!p || typeof p[metodo] !== "function") return;
      p[metodo]("ended", alTerminarPista);
      p[metodo]("mute", alSilenciar);
      p[metodo]("unmute", alReactivar);
    });
    if (ventana && typeof ventana[metodo] === "function") ventana[metodo]("online", alVolverLaRed);
    if (doc && typeof doc[metodo] === "function") doc[metodo]("visibilitychange", alCambiarVisibilidad);
  }

  function pararTodo() {
    pararLatido();
    pararVigia();
    if (timerReintento) reloj.clearTimeout(timerReintento);
    timerReintento = null;
    soltarTimerPausa();
    if (timerHuecos) reloj.clearTimeout(timerHuecos);
    timerHuecos = null;
    escuchar(false);
  }

  // ── API pública ──

  /**
   * Abre la sesión en el servidor y empieza a grabar. Con otra persona del
   * equipo grabando desde otro dispositivo el servidor responde
   * "otro_grabando" (esOtroGrabando de api.js): `forzar` graba igual.
   * `continuaDe`: "Seguir grabando" después de un corte, la sesión anterior de
   * este dispositivo (sigue subiendo lo último: el servidor no la cierra). Con
   * `dispositivoId` (el de crearGrabadora) el servidor reconoce una recarga.
   * @param {{forzar?: boolean, continuaDe?: string|null}} [o]
   */
  async function iniciar({ forzar = false, continuaDe = null } = {}) {
    if (fase !== "inactiva") throw new Error("La grabación ya empezó.");
    if (!MR) throw new Error("Este navegador no puede grabar audio.");
    fase = "iniciando";
    avisar();
    let r;
    try {
      r = await api.salaIniciarSesion({
        aid,
        mimeType: formato.mimeType,
        ...(forzar === true ? { forzar: true } : {}),
        ...(typeof continuaDe === "string" && continuaDe ? { continuaDe } : {}),
        ...(typeof dispositivoId === "string" && dispositivoId ? { dispositivoId } : {}),
      });
    } catch (e) {
      fase = "inactiva";
      avisar();
      throw e;
    }
    if (!r || !r.sesionId) {
      fase = "inactiva";
      avisar();
      throw new Error("El servidor no abrió la grabación.");
    }
    sesionId = String(r.sesionId);
    ext = r.ext || formato.ext;
    try {
      recorder = new MR(stream, { mimeType: formato.mimeType, audioBitsPerSecond: formato.bitsPorSegundo });
      conectar(recorder);
      recorder.start(TIMESLICE_MS);
    } catch (e) {
      // No se grabó nada: queda "detenida" para que finalizar() no espere una cola que nunca llega.
      fase = "error";
      detenida = true;
      ultimoError = "Este navegador no pudo empezar a grabar.";
      avisar();
      throw e;
    }
    iniciadaEnMs = reloj.ahora();
    ultimoDatoMs = iniciadaEnMs;
    fase = "grabando";
    escuchar(true);
    if (wakeLock && typeof wakeLock.pedir === "function") wakeLock.pedir();
    programarLatido();
    programarVigia();
    avisar();
    return { sesionId, ext };
  }

  /** Para el grabador y deja en la cola el último pedazo. */
  async function detener() {
    if (fase !== "grabando" || !recorder) return;
    // Una pausa abierta (se terminó con la pantalla recién desbloqueada) cierra acá.
    if (pausa) {
      if (pistaSilenciada()) pausa.silenciada = true;
      cerrarPausa(pausa.vueltaMs != null && !pistaSilenciada() ? pausa.vueltaMs : tiempoMs());
    }
    fase = "deteniendo";
    // Paró a pedido: el servidor se entera ya (no es un corte, no hay que avisar a nadie).
    programarLatido(0);
    avisar();
    const listo = new Promise((res) => {
      resolverStop = res;
    });
    try {
      if (recorder.state !== "inactive") recorder.stop();
      else resolverStop();
    } catch (_) {
      if (resolverStop) resolverStop();
    }
    await conTope(listo, ESPERA_STOP_MS);
    if (!detenida) {
      // El navegador no avisó el stop: se sigue con lo que haya.
      resolverStop = null;
      cerrarObjeto();
      detenida = true;
      detenidaEnMs = reloj.ahora();
    }
    pararVigia();
    if (fase === "deteniendo") fase = "subiendo";
    avisar();
    bombear();
    revisarEsperas();
  }

  /**
   * Termina la sesión: para el grabador (si sigue), espera que TODO esté
   * subido y confirmado ("100 % subido") y la cierra con salaFinalizarSesion.
   * Si el servidor dice que faltan partes, las vuelve a subir (siguen en
   * memoria). Sin red, espera reintentando: la promesa termina cuando la sesión
   * quedó completa. Llamarla dos veces devuelve la misma promesa.
   * @returns {Promise<{ok: true, audio?: object}|{vacia: true}|{faltantes: number[]}>}
   */
  function finalizar() {
    if (finalizando) return finalizando;
    // Sin sesión todavía no hay nada que cerrar (y no se guarda: puede empezar después).
    if (fase === "inactiva" || fase === "iniciando") return Promise.resolve({ vacia: true });
    finalizando = (async () => {
      if (fase === "grabando") await detener();
      if (fase === "interrumpida" || fase === "deteniendo") {
        // Terminar (o Seguir grabando) después de un corte: deja de ser un corte
        // sin atender, y el servidor se entera con un latido ya.
        if (fase === "interrumpida") programarLatido(0);
        fase = "subiendo";
      }
      avisar();
      bombear();
      let rondas = 0;
      let intentos = 0;
      let rondasHuecos = 0;
      for (;;) {
        await esperarCola();
        if (destruida) return { cancelada: true };
        if (!objetos.length) {
          fase = "error";
          ultimoError = "La grabación quedó vacía.";
          pararTodo();
          avisar();
          return { vacia: true };
        }
        // Los huecos van antes del cierre (el worker los lee de la sesión). Si la
        // red no los deja pasar en unos intentos, se cierra igual: el audio manda.
        if (huecosPendientes() && rondasHuecos < MAX_RONDAS_HUECOS) {
          rondasHuecos += 1;
          await enviarHuecosYa();
          if (destruida) return { cancelada: true };
          if (huecosPendientes()) {
            await dormir(esperaReintento(rondasHuecos));
            continue;
          }
        }
        fase = "finalizando";
        avisar();
        let r;
        try {
          r = (await api.salaFinalizarSesion({ aid, sesionId, totalChunks: objetos.length })) || {};
        } catch (e) {
          if (destruida) return { cancelada: true };
          if (!esReintentable(e)) {
            fase = "error";
            ultimoError = mensajeDeError(e);
            pararTodo();
            avisar();
            throw e;
          }
          intentos += 1;
          sinConexion = esErrorDeRed(e);
          ultimoError = mensajeDeError(e);
          avisar();
          await dormir(esperaReintento(intentos));
          continue;
        }
        const faltantes = Array.isArray(r.faltantes)
          ? [...new Set(r.faltantes.map(Number))].filter((n) => Number.isInteger(n) && n >= 0 && n < objetos.length)
          : [];
        if (r.ok !== true && faltantes.length) {
          rondas += 1;
          if (rondas > MAX_RONDAS_FALTANTES) {
            fase = "error";
            ultimoError = "El servidor no recibe algunas partes de la grabación.";
            pararTodo();
            avisar();
            return { faltantes };
          }
          faltantes.forEach((n) => {
            const o = objetos[n];
            o.estado = "en_cola";
            o.subido = false;
            o.url = null;
            o.intentos = 0;
            o.reintentoEnMs = 0;
          });
          fase = "subiendo";
          avisar();
          bombear();
          continue;
        }
        resultado = r;
        fase = "completa";
        sinConexion = false;
        ultimoError = null;
        pararTodo();
        avisar();
        return r;
      }
    })();
    // Después de un error se puede volver a intentar.
    finalizando.catch(() => {
      finalizando = null;
    });
    return finalizando;
  }

  /** Todo lo grabado hasta ahora en un solo archivo (los objetos en orden + lo que está en el grabador). */
  function copia() {
    return new Blob([...objetos.map((o) => o.blob), ...partes], { type: formato.mimeType });
  }

  /** Milisegundos desde que empezó a grabar (para los marcadores de "Siguiente punto"). */
  function tiempoMs() {
    if (iniciadaEnMs == null) return 0;
    return Math.max(0, (detenidaEnMs != null ? detenidaEnMs : reloj.ahora()) - iniciadaEnMs);
  }

  function estado() {
    let confirmados = 0;
    let bytesConfirmados = 0;
    let bytesTotales = 0;
    let conError = false;
    objetos.forEach((o) => {
      bytesTotales += o.bytes;
      if (o.estado === "confirmado") {
        confirmados += 1;
        bytesConfirmados += o.bytes;
      } else if (o.conError) {
        conError = true;
      }
    });
    partes.forEach((b) => {
      bytesTotales += b.size;
    });
    const todoSubido = fase === "completa" || colaVacia();
    let porcentaje = 0;
    if (todoSubido && objetos.length) porcentaje = 100;
    else if (bytesTotales > 0) porcentaje = Math.min(99, Math.floor((bytesConfirmados * 100) / bytesTotales));
    return {
      fase,
      sesionId,
      ext,
      iniciadaEnMs,
      detenidaEnMs,
      interrupcion,
      microfono,
      objetos: objetos.length,
      confirmados,
      pendientes: objetos.length - confirmados,
      bytesTotales,
      bytesConfirmados,
      porcentaje,
      todoSubido,
      conError,
      sinConexion: sinConexion || latidoFallando,
      sesionRechazada,
      ultimoError,
      resultado,
      // Ratos sin audio de esta sesión (desde su inicio) y si llegaron al servidor.
      huecos: huecos.map((h) => ({ desdeMs: h.desdeMs, hastaMs: h.hastaMs, causa: h.causa, enviado: h.estado === "enviado" })),
      // Pausa en curso (todavía sin cerrar): desde cuándo.
      pausa: pausa ? { desdeMs: pausa.desdeMs } : null,
    };
  }

  /** Corta todo sin cerrar la sesión (la página se va): lo no subido se pierde. */
  function destruir() {
    if (destruida) return;
    destruida = true;
    pararTodo();
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch (_) {
        /* ya parado */
      }
    }
    revisarEsperas();
    subs.clear();
  }

  return {
    formato: { ...formato },
    iniciar,
    detener,
    finalizar,
    reintentarAhora,
    copia,
    tiempoMs,
    estado,
    sesionId: () => sesionId,
    suscribir(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    destruir,
  };
}

// ────────────────────────── Marcadores ("Siguiente punto") ──────────────────────────

/**
 * Cola de marcadores: se mandan de a uno y en orden, con reintentos mientras
 * el error sea de la red (o de la sesión de Firebase). Si el servidor rechaza
 * uno (punto inexistente, fuera de la ventana), se descarta y se avisa en
 * ultimoError (queda a la vista aunque los siguientes se manden bien): no
 * traba a los siguientes. Un error de red se borra con el próximo envío.
 * Cada marcador guarda cuándo se tocó (tocadoEnMs, del reloj de este
 * dispositivo) y cada intento manda atrasoMs = ahora − tocadoEnMs: uno que
 * esperó un corte de red se ubica en la hora del toque, no en la de llegada
 * (el servidor resta el atraso a su propia hora, así no importa si el reloj
 * del celular está corrido).
 * @param {{enviar: (m: object) => Promise<any>, reloj?: object, alCambiar?: () => void}} p
 */
export function crearColaMarcadores({ enviar, reloj = relojReal, alCambiar } = {}) {
  const cola = [];
  let enviando = false;
  let timer = null;
  let intentos = 0;
  let ultimoError = null;
  let errorEsRechazo = false;
  let destruida = false;
  const avisar = () => {
    if (!alCambiar) return;
    try {
      alCambiar();
    } catch (_) {
      /* nada */
    }
  };

  function programarReintento(ms) {
    if (timer) reloj.clearTimeout(timer);
    timer = reloj.setTimeout(() => {
      timer = null;
      bombear();
    }, ms);
  }

  async function bombear() {
    if (enviando || destruida) return;
    enviando = true;
    try {
      while (cola.length && !destruida) {
        try {
          const { tocadoEnMs, ...marcador } = cola[0];
          await enviar({ ...marcador, atrasoMs: Math.max(0, reloj.ahora() - tocadoEnMs) });
          cola.shift();
          intentos = 0;
          if (!errorEsRechazo) ultimoError = null;
          avisar();
        } catch (e) {
          if (destruida) return;
          if (!esReintentable(e)) {
            cola.shift();
            intentos = 0;
            ultimoError = mensajeDeError(e);
            errorEsRechazo = true;
            avisar();
            continue;
          }
          intentos += 1;
          ultimoError = mensajeDeError(e);
          errorEsRechazo = false;
          avisar();
          programarReintento(esperaReintento(intentos));
          break;
        }
      }
    } finally {
      enviando = false;
    }
  }

  return {
    encolar(m) {
      const tocado = m && Number.isFinite(m.tocadoEnMs) ? m.tocadoEnMs : reloj.ahora();
      cola.push({ ...m, tocadoEnMs: tocado });
      avisar();
      bombear();
    },
    pendientes: () => cola.length,
    ultimoError: () => ultimoError,
    reintentarAhora() {
      if (timer) {
        reloj.clearTimeout(timer);
        timer = null;
      }
      bombear();
    },
    destruir() {
      destruida = true;
      if (timer) reloj.clearTimeout(timer);
      timer = null;
    },
  };
}
